/**
 * Admin · Customer Licences (light-mode).
 * At /admin/licences — Panorix superadmin issues, resends, rotates, revokes customer licences.
 */
import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Search, Plus, RotateCw, Ban, Copy, ExternalLink, Send, ShieldCheck, Users2, Clock3, Activity } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/lib/api';

const TIERS = [
  { value: 'trial',        label: 'Trial (30-day)' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise',   label: 'Enterprise (default)' },
  { value: 'air_gapped',   label: 'Air-gapped' },
];

// Light-mode status badge tones
const STATUS_STYLES = {
  PENDING:   'bg-amber-50 text-amber-700 border-amber-200',
  ACCEPTED:  'bg-blue-50 text-blue-700 border-blue-200',
  ACTIVATED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  ROTATED:   'bg-slate-100 text-slate-600 border-slate-300',
  REVOKED:   'bg-red-50 text-red-700 border-red-200',
  EXPIRED:   'bg-red-50 text-red-700 border-red-200',
};
const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border ${STATUS_STYLES[status] || STATUS_STYLES.ROTATED}`}>
    {status}
  </span>
);

// Light-mode UI classes
const inp = "px-3 py-2 rounded-md text-sm bg-white border border-slate-300 text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]/30 focus:border-[#1E4E8C] placeholder:text-slate-400";
const btnPrimary = "inline-flex items-center gap-2 px-4 py-2 rounded-md text-[12px] tracking-[0.1em] uppercase font-semibold bg-[#F26B1F] hover:bg-[#D85A0F] text-white transition shadow-sm";
const btnGhost   = "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition";
const btnDanger  = "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] text-red-600 hover:text-red-700 hover:bg-red-50 transition";

const licenceApi = {
  list:    (params) => api.get('/admin/licences', { params }).then(r => r.data),
  metrics: ()       => api.get('/admin/licences/metrics').then(r => r.data),
  detail:  (uuid)   => api.get(`/admin/licences/${uuid}`).then(r => r.data),
  issue:   (body)   => api.post('/admin/licences/issue', body).then(r => r.data),
  resend:  (uuid)   => api.post(`/admin/licences/${uuid}/resend-link`).then(r => r.data),
  rotate:  (uuid)   => api.post(`/admin/licences/${uuid}/rotate`).then(r => r.data),
  revoke:  (uuid, reason) => api.post(`/admin/licences/${uuid}/revoke`, { reason }).then(r => r.data),
  moduleCatalog: () => api.get('/admin/modules').then(r => r.data),
  deployOptions: () => api.get('/aws-deploy/options').then(r => r.data),
  projects:      () => api.get('/migration/projects').then(r => r.data),
};

const emptyForm = {
  customer_name: '', customer_contact_email: '', customer_contact_name: '',
  tier: 'enterprise', seats: 50, valid_days: 365, modules: [], send_email: true,
  cloud: 'both', aws_region: 'ap-south-1',
  bedrock_model_id: 'global.anthropic.claude-opus-5',
  migrate_project_ids: [], migrate_files: true,
};

const CLOUDS = [
  { value: 'both',  label: 'Azure or AWS — their choice' },
  { value: 'aws',   label: 'AWS only' },
  { value: 'azure', label: 'Azure only' },
];

// A single stat card, light-themed.
const MetricCard = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-slate-500">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      {label}
    </div>
    <div className="text-3xl font-light tabular-nums text-slate-900 mt-2" style={{ color: accent || undefined }}>
      {value}
    </div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);


export default function AdminLicences() {
  const [rows, setRows] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [moduleCatalog, setModuleCatalog] = useState([]);   // [{code, name, category}]
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [issueOpen, setIssueOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [issued, setIssued] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deployOptions, setDeployOptions] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    licenceApi.deployOptions().then(setDeployOptions).catch(() => {});
    licenceApi.projects().then(d => setProjects(d.projects || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const [list, mx, mc] = await Promise.all([
        licenceApi.list(params),
        licenceApi.metrics().catch(() => null),
        licenceApi.moduleCatalog().catch(() => null),
      ]);
      setRows(list.licences || []);
      setMetrics(mx);
      if (mc && Array.isArray(mc.modules)) {
        const cat = mc.modules
          .map(m => ({
            code:     (m.moduleCode || '').toUpperCase(),
            name:     m.moduleName || m.moduleCode,
            category: m.moduleCategory || 'GENERAL',
          }))
          .filter(m => m.code);
        // De-dup + stable sort by category, then name
        const seen = new Set();
        const uniq = cat.filter(m => !seen.has(m.code) && seen.add(m.code));
        uniq.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name));
        setModuleCatalog(uniq);
      }
    } catch { toast.error('Failed to load licences'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, status]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, seats: Number(form.seats), valid_days: Number(form.valid_days) };
      const res = await licenceApi.issue(body);
      if (res.email_sent) {
        toast.success(`Licence issued · email sent to ${res.customer_email}`);
      } else if (res.email_error) {
        // Deliberately long-lived toast so the admin can read the exact Resend reason
        // (e.g. "domain not verified"). We still show the collect-URL modal below so
        // the licence itself is fully usable — only the delivery failed.
        toast.error(`Licence issued but email failed: ${res.email_error}`, { duration: 12000 });
      } else {
        toast.success(`Licence issued for ${form.customer_name}`);
      }
      setIssued(res);
      setIssueOpen(false);
      setForm(emptyForm);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const doResend = async (r) => {
    try { const res = await licenceApi.resend(r.licence_uuid); setIssued(res); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const doRotate = async (r) => {
    if (!window.confirm(`Rotate ${r.customer_name}'s licence? This invalidates the current key.`)) return;
    try { const res = await licenceApi.rotate(r.licence_uuid); setIssued({...res, collect_url: res.collect_url}); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const doRevoke = async (r) => {
    const reason = window.prompt(`Revoke ${r.customer_name}'s licence? Enter reason:`, 'manual revoke');
    if (!reason) return;
    try { await licenceApi.revoke(r.licence_uuid, reason); toast.success('Revoked'); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const openDetail = async (r) => {
    try { const d = await licenceApi.detail(r.licence_uuid); setDetail(d); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const toggleModule = (m) => setForm(f => ({
    ...f, modules: f.modules.includes(m) ? f.modules.filter(x => x !== m) : [...f.modules, m]
  }));

  return (
    <AdminLayout title="Customer Licences" subtitle="Admin · Enterprise Licence Portal">
      <div className="bg-[#F8FAFC] -mx-6 -my-6 px-6 py-6 min-h-screen">

        {/* Prominent header with Issue button */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Customer Licences</h1>
            <p className="text-sm text-slate-500 mt-1">Issue and manage enterprise licences with magic-link delivery.</p>
          </div>
          <button onClick={() => setIssueOpen(true)} className={btnPrimary} data-testid="licences-issue-btn">
            <Plus className="w-4 h-4"/>Issue Licence
          </button>
        </div>

        {/* Metrics */}
        {metrics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="licence-metrics">
            <MetricCard icon={ShieldCheck} label="Active Licences" value={metrics.active_licences} sub={`${metrics.pending} pending · ${metrics.revoked} revoked`} accent="#10B981"/>
            <MetricCard icon={Users2} label="Active Seats" value={metrics.active_seats.toLocaleString()} sub={`${metrics.total_seats.toLocaleString()} contracted`} accent="#1E4E8C"/>
            <MetricCard icon={Clock3} label="Near Expiry" value={metrics.near_expiry_30d} sub="Within next 30 days" accent="#F26B1F"/>
            <MetricCard icon={Activity} label="Heartbeats 24h" value={metrics.heartbeats_24h} sub={`${metrics.heartbeats_7d} in last 7 days`} accent="#5790C9"/>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
            <input className={`${inp} pl-9 w-full`} placeholder="Search customer, email, or key…"
                   value={q} onChange={e => setQ(e.target.value)} data-testid="licences-search"/>
          </div>
          <select className={inp} value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLES).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="licences-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Customer','Tier','Seats','Key','Status','Issued','Expires',''].map(h =>
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-medium tracking-[0.2em] text-slate-500 uppercase whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="8" className="text-center py-12 text-slate-500 text-sm">
                    {loading ? 'Loading…' : 'No licences yet — click Issue Licence above to create the first one.'}
                  </td></tr>
                ) : rows.map((r) => (
                  <tr key={r.licence_uuid} className="border-b border-slate-100 hover:bg-slate-50/70 transition">
                    <td className="px-4 py-3">
                      <div className="text-slate-900">{r.customer_name}</div>
                      <div className="text-[11px] text-slate-500">{r.customer_contact_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider border bg-blue-50 text-blue-700 border-blue-200">
                        {(r.tier || '').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-700">{r.seats}</td>
                    <td className="px-4 py-3"><span className="font-mono text-[11px] text-slate-500">{r.licence_key}</span></td>
                    <td className="px-4 py-3"><StatusBadge status={r.status}/></td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 whitespace-nowrap">{new Date(r.issued_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-[11px] text-slate-500 whitespace-nowrap">{new Date(r.expires_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openDetail(r)}   className={btnGhost} data-testid={`view-${r.licence_uuid}`}>View</button>
                        {['PENDING','ACCEPTED','ACTIVATED'].includes(r.status) && (
                          <>
                            <button onClick={() => doResend(r)} className={btnGhost} data-testid={`resend-${r.licence_uuid}`}><Send className="w-3.5 h-3.5"/>Resend</button>
                            <button onClick={() => doRotate(r)} className={btnGhost} data-testid={`rotate-${r.licence_uuid}`}><RotateCw className="w-3.5 h-3.5"/>Rotate</button>
                            <button onClick={() => doRevoke(r)} className={btnDanger} data-testid={`revoke-${r.licence_uuid}`}><Ban className="w-3.5 h-3.5"/>Revoke</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Issue dialog — light, wider */}
        <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-slate-900">Issue New Licence</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[['Customer name','customer_name','text','Al Dhafra Refinery Ops',true],
                  ['Architect email','customer_contact_email','email','architect@customer.com',true],
                  ['Architect name','customer_contact_name','text','Full name (optional)',false]
                ].map(([l,k,t,ph,req]) => (
                  <div key={k} className={k === 'customer_contact_name' ? 'sm:col-span-2' : ''}>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</label>
                    <input type={t} required={req} className={`${inp} w-full mt-1`} placeholder={ph}
                           value={form[k]} onChange={e => setForm(f => ({...f, [k]: e.target.value}))}
                           data-testid={`issue-${k}`}/>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Tier</label>
                  <select className={`${inp} w-full mt-1`} value={form.tier}
                          onChange={e => setForm(f => ({...f, tier: e.target.value}))}>
                    {TIERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Seats</label>
                  <input type="number" min="1" className={`${inp} w-full mt-1`} value={form.seats}
                         onChange={e => setForm(f => ({...f, seats: e.target.value}))} data-testid="issue-seats"/>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Valid (days)</label>
                  <input type="number" min="7" max="1825" className={`${inp} w-full mt-1`} value={form.valid_days}
                         onChange={e => setForm(f => ({...f, valid_days: e.target.value}))}/>
                </div>
              </div>

              {/* Modules — dynamic catalog, grouped by category, with Select All / Clear */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Modules {moduleCatalog.length > 0 && <span className="text-slate-400 normal-case tracking-normal">· {form.modules.length}/{moduleCatalog.length} selected</span>}
                  </label>
                  <div className="flex items-center gap-2">
                    <button type="button" data-testid="modules-select-all"
                            onClick={() => setForm(f => ({...f, modules: moduleCatalog.map(m => m.code)}))}
                            className="text-[11px] px-2.5 py-1 rounded border border-[#1E4E8C] text-[#1E4E8C] hover:bg-[#1E4E8C] hover:text-white transition">
                      Select all
                    </button>
                    <button type="button" data-testid="modules-clear-all"
                            onClick={() => setForm(f => ({...f, modules: []}))}
                            className="text-[11px] px-2.5 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition">
                      Clear
                    </button>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 p-3 max-h-64 overflow-y-auto space-y-3" data-testid="modules-catalog">
                  {moduleCatalog.length === 0 ? (
                    <div className="text-xs text-slate-500 py-2">Loading module catalog…</div>
                  ) : (
                    Object.entries(
                      moduleCatalog.reduce((acc, m) => {
                        (acc[m.category] = acc[m.category] || []).push(m); return acc;
                      }, {})
                    ).map(([cat, mods]) => (
                      <div key={cat}>
                        <div className="text-[9px] tracking-[0.25em] uppercase text-slate-400 mb-1.5">{cat.replace(/_/g, ' ')}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {mods.map(m => {
                            const active = form.modules.includes(m.code);
                            return (
                              <button type="button" key={m.code} onClick={() => toggleModule(m.code)}
                                data-testid={`module-chip-${m.code}`}
                                title={m.name}
                                className={`text-[11px] px-2.5 py-1 rounded border transition ${
                                  active
                                    ? 'bg-[#1E4E8C] border-[#1E4E8C] text-white'
                                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}>
                                {m.code}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={form.send_email}
                       onChange={e => setForm(f => ({...f, send_email: e.target.checked}))}/>
                Email the magic-link to the architect (uses Resend if configured)
              </label>

              {/* Where they will deploy it, and what data travels with the licence */}
              <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="licence-deployment-block">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Deployment
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Cloud</label>
                    <select className={`${inp} w-full mt-1`} value={form.cloud}
                            data-testid="licence-cloud-select"
                            onChange={e => setForm(f => ({...f, cloud: e.target.value}))}>
                      {CLOUDS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">AWS region</label>
                    <select className={`${inp} w-full mt-1`} value={form.aws_region}
                            data-testid="licence-region-select"
                            onChange={e => setForm(f => ({...f, aws_region: e.target.value}))}>
                      {(deployOptions?.regions || [{ code: 'ap-south-1', name: 'Mumbai' }])
                        .map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Claude on Bedrock — model routing
                  </label>
                  <select className={`${inp} w-full mt-1`} value={form.bedrock_model_id}
                          data-testid="licence-bedrock-select"
                          onChange={e => setForm(f => ({...f, bedrock_model_id: e.target.value}))}>
                    {(deployOptions?.bedrockProfiles || []).map(p => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Runs in the customer's own AWS account, so no Claude key changes hands.
                    They can change this on the licence page before launching.
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      Projects to migrate with the deployment
                      <span className="text-slate-400 normal-case tracking-normal"> · {form.migrate_project_ids.length} selected</span>
                    </label>
                    {form.migrate_project_ids.length > 0 && (
                      <button type="button" data-testid="licence-projects-clear"
                              onClick={() => setForm(f => ({...f, migrate_project_ids: []}))}
                              className="text-[11px] px-2.5 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition">
                        Clear
                      </button>
                    )}
                  </div>
                  <div className="rounded border border-slate-200 p-2 max-h-40 overflow-y-auto space-y-1"
                       data-testid="licence-projects-list">
                    {projects.length === 0 ? (
                      <div className="text-xs text-slate-500 py-1">No projects to carry.</div>
                    ) : projects.map(p => (
                      <label key={p.id} className="flex items-center gap-2 text-[12px] text-slate-700 cursor-pointer"
                             data-testid={`licence-project-${p.id}`}>
                        <input type="checkbox"
                               checked={form.migrate_project_ids.includes(p.id)}
                               onChange={() => setForm(f => ({
                                 ...f,
                                 migrate_project_ids: f.migrate_project_ids.includes(p.id)
                                   ? f.migrate_project_ids.filter(x => x !== p.id)
                                   : [...f.migrate_project_ids, p.id],
                               }))}/>
                        <span className="truncate">{p.name}</span>
                      </label>
                    ))}
                  </div>
                  <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-2">
                    <input type="checkbox" checked={form.migrate_files}
                           data-testid="licence-migrate-files"
                           onChange={e => setForm(f => ({...f, migrate_files: e.target.checked}))}/>
                    Carry those projects' documents and drawings too
                  </label>
                  <p className="text-[11px] text-slate-500 mt-1">
                    Leave empty for a clean install. Anything ticked is packed into a bundle
                    the new deployment loads on its first start.
                  </p>
                </div>
              </div>

              <button type="submit" className={`${btnPrimary} w-full justify-center`} data-testid="issue-submit">
                Issue Licence
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Post-issue "here's the link" modal */}
        <Dialog open={!!issued} onOpenChange={(o) => !o && setIssued(null)}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg">
            <DialogHeader><DialogTitle>Licence issued · share the link</DialogTitle></DialogHeader>
            {issued && (
              <div className="space-y-4">
                <div className="text-sm text-slate-600">
                  {issued.email_sent
                    ? `An email has been sent to ${issued.customer_email} with this collect link.`
                    : (issued.email_error
                        ? <>Email delivery failed. Share this link manually. <span className="block mt-1 text-red-600 text-[11px]">Reason: {issued.email_error}</span></>
                        : 'Resend is not configured — share this link manually with the architect.')}
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Collect URL (single-use, 72 h)</div>
                  <div className="flex items-center gap-2">
                    <input readOnly value={issued.collect_url}
                           className={`${inp} flex-1 text-[11px] font-mono`}/>
                    <button onClick={() => { navigator.clipboard.writeText(issued.collect_url); toast.success('Copied'); }}
                            className={btnGhost}><Copy className="w-3.5 h-3.5"/>Copy</button>
                    <a href={issued.collect_url} target="_blank" rel="noopener noreferrer"
                       className={btnGhost}><ExternalLink className="w-3.5 h-3.5"/>Open</a>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">Link expires {issued.collect_url_expires_at ? new Date(issued.collect_url_expires_at).toLocaleString() : ''}. First click starts a 24 h session for the architect.</div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Detail drawer */}
        <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
          <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl">
            <DialogHeader><DialogTitle>{detail?.licence?.customer_name}</DialogTitle></DialogHeader>
            {detail && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[['Email', detail.licence.customer_contact_email],
                    ['Tier', detail.licence.tier?.toUpperCase()],
                    ['Seats', detail.licence.seats],
                    ['Status', detail.licence.status],
                    ['Issued', new Date(detail.licence.issued_at).toLocaleString()],
                    ['Expires', new Date(detail.licence.expires_at).toLocaleString()],
                    ['Reveals', detail.licence.reveal_count],
                    ['Last heartbeat', detail.licence.last_heartbeat_at ? new Date(detail.licence.last_heartbeat_at).toLocaleString() : 'never'],
                    ['Fingerprint', detail.licence.machine_fingerprint ? detail.licence.machine_fingerprint.slice(0, 24) + '…' : 'not yet activated'],
                  ].map(([l,v]) => (
                    <div key={l}>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{l}</div>
                      <div className="text-slate-800 text-sm mt-1">{v ?? '—'}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Audit trail (last 50)</div>
                  <div className="max-h-64 overflow-y-auto rounded border border-slate-200">
                    {(detail.audit_events || []).length === 0 ? (
                      <div className="text-slate-500 text-xs p-3">No events yet</div>
                    ) : (
                      <table className="w-full text-[11px]">
                        <tbody>
                          {detail.audit_events.map(e => (
                            <tr key={e.audit_id} className="border-b border-slate-100">
                              <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{new Date(e.ts).toLocaleString()}</td>
                              <td className="px-3 py-1.5 text-slate-800">{e.action}</td>
                              <td className="px-3 py-1.5 text-slate-500">{e.actor_email}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
